<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import MacroPreview from './MacroPreview.vue';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const isExecuting = ref(false);
const showPreview = ref(false);

const executeMacro = async macro => {
  try {
    isExecuting.value = true;
    await store.dispatch('macros/execute', {
      macroId: macro.id,
      conversationIds: [props.conversationId],
    });
    useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
    useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  } finally {
    isExecuting.value = false;
  }
};

const toggleMacroPreview = () => {
  showPreview.value = !showPreview.value;
};

</script>

<template>
  <div
    class="mb-2 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
  >
    <div class="flex min-h-12 items-center gap-2 px-3 py-2">
      <span class="i-lucide-grip-vertical drag-handle shrink-0 cursor-grab text-n-slate-9" />
      <span class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12">
        {{ macro.name }}
      </span>
      <div class="flex shrink-0 items-center gap-1">
        <NextButton
          v-tooltip.left-start="$t('MACROS.EXECUTE.PREVIEW')"
          :aria-label="$t('MACROS.EXECUTE.PREVIEW')"
          :aria-expanded="showPreview"
          :icon="showPreview ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          slate
          ghost
          xs
          @click="toggleMacroPreview"
        />
        <NextButton
          v-tooltip.left-start="$t('MACROS.EXECUTE.BUTTON_TOOLTIP')"
          :aria-label="$t('MACROS.EXECUTE.BUTTON_TOOLTIP')"
          icon="i-lucide-play"
          blue
          faded
          xs
          :is-loading="isExecuting"
          @click="executeMacro(macro)"
        />
      </div>
    </div>
    <MacroPreview v-if="showPreview" :macro="macro" />
  </div>
</template>
